import { DOCUMENT } from '@angular/common';
import { Component, Inject, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { ActivatedRoute, NavigationStart, Router } from '@angular/router';
import { fade, openClose } from '@core/animations/animations';
import { ICON } from '@core/util/icon';
import { Matter } from '@models/matter';
import { UserMatterViewHistory } from '@models/userViewHistory';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { SelectSnapshot } from '@ngxs-labs/select-snapshot';
import { Select, Store } from '@ngxs/store';
import { TrackLocationService } from '@services/matter/track-location.service';
import { UrlService } from '@services/url/url.service';
import { ChicagoSelectors } from '@state/global/title-insurance/chicago';
import { FCTSelectors } from '@state/global/title-insurance/fct';
import { StewartSelectors } from '@state/global/title-insurance/stewart';
import { TitlePlusSelectors } from '@state/global/title-insurance/title-plus';
import { MatterDispatch } from '@state/matter';
import { SelectedMatter } from '@state/matter/matter.selectors';
import { NavigationState } from '@state/navigation/navigation.state';
import { SITE_SECTION } from '@state/navigation/page.model';
import { RecentlyViewedDispatch } from '@state/recently-viewed/recently-viewed.dispatch';
import { UserProfileState } from '@state/user-profile';
import { combineLatest, firstValueFrom, Observable } from 'rxjs';
import { distinctUntilChanged, filter, map } from 'rxjs/operators';
import { NavigationService } from 'src/app/shared/services/nav/nav.service';
import { TestHooks } from './sale.hooks';
import { SaleComponentSelector } from './sale.selector';
import { LeapSdkService } from '../../public/leap/leap-sdk.service';
import { Logger } from '@core/logger';
import { LeapApiService } from '../../shared/api-repos/leap/leap-api.service';
import { LeapApiService as LeapService } from '../../public/leap/leap-api.service';
import { isNotNullOrWhitespace } from '@core/util/string';
import { SnackbarService } from '@services/ui/snackbar.service';

@UntilDestroy()
@Component({
	selector: 'app-sale',
	templateUrl: './sale.component.html',
	styleUrls: ['./sale.component.scss'],
	animations: [fade(), openClose()],
	standalone: false,
})
export class SaleComponent implements OnInit, OnDestroy {
	testHooks = TestHooks;
	public WARNING_ICON = ICON.WARNING;
	public showMessage$: Observable<string>;
	public matterId: string;
	@Select(SaleComponentSelector.GetHistoryObject)
	matterHistory$: Observable<UserMatterViewHistory>;
	@Select(SaleComponentSelector.ShowRecentlyOpened)
	showRecentlyOpened$: Observable<boolean>;
	@Select(SelectedMatter.id) matterId$: Observable<string>;
	@Select(SelectedMatter.loading) matterLoading$: Observable<boolean>;
	public matterLoading: boolean;
	@Select(StewartSelectors.payloadWarning)
	showStewartPayloadWarning$: Observable<boolean>;
	@Select(ChicagoSelectors.getPayloadHasChanged)
	showChicagoPayloadWarning$: Observable<boolean>;
	@Select(FCTSelectors.getPayloadHasChanged)
	showFCTPayloadWarning$: Observable<boolean>;
	@Select(TitlePlusSelectors.getPayloadHasChanged)
	showTitlePlusPayloadWarning$: Observable<boolean>;
	@SelectSnapshot(SelectedMatter.isLeapMatter) isLeapMatter: boolean;
	@Select(SelectedMatter.getLeapMatterId) leapMatterId$: Observable<string>;
	@Select(SelectedMatter.getSelected) matter$: Observable<Matter>;
	public showNavigation = true;
	@Select(NavigationState.getSiteSection) routeData$: Observable<SITE_SECTION>;
	public loadingMessage: string;

	constructor(
		private recentlyViewedDispatch: RecentlyViewedDispatch,
		private renderer: Renderer2,
		private navigationService: NavigationService,
		private trackLocationService: TrackLocationService,
		private route: ActivatedRoute,
		private router: Router,
		private matterDispatch: MatterDispatch,
		private dialog: MatDialog,
		private store: Store,
		@Inject(DOCUMENT) private document: Document,
		private leapSdkService: LeapSdkService,
		private leapApiService: LeapApiService,
		private leapService: LeapService,
		private snackbarService: SnackbarService,
	) {
		this.routeData$.pipe(untilDestroyed(this)).subscribe((section) => {
			this.showNavigation = section !== SITE_SECTION.MatterPackage;
		});
	}

	async ngOnInit(): Promise<void> {
		//This captures navigation events to go outside of the matter.
		this.router.events
			.pipe(
				untilDestroyed(this),
				// eslint-disable-next-line @typescript-eslint/no-explicit-any
				filter((event: any) => {
					return (
						event instanceof NavigationStart &&
						!(event as NavigationStart).url.includes(UrlService.Matter(''))
					);
				}),
			)
			.subscribe((event: NavigationStart) => {
				if (this.matterId && this.isLeapMatter) {
					Logger.info(
						`Switching from matter ${this.matterId} to ${event.url}. Sending changes to LEAP for matter ${this.matterId}.`,
					);
					firstValueFrom(this.leapApiService.sendToLeap(this.matterId));
				}
			});

		// This captures navigation to a matter from anywhere.
		this.route.paramMap
			.pipe(
				untilDestroyed(this),
				map((params) => params.get('matterId')),
				filter((matterId) => !!matterId),
			)
			.subscribe(async (matterId) => {
				if (this.matterId && this.isLeapMatter) {
					Logger.info(
						`Switching from matter ${this.matterId} to ${matterId}. Sending changes to LEAP for matter ${this.matterId}!`,
					);
					firstValueFrom(this.leapApiService.sendToLeap(this.matterId));
				}
				this.matterLoading = true;
				this.matterDispatch.Get(matterId);
				this.matterId = matterId;
				const lastMatterLocation =
					this.trackLocationService.getLastLocationForMatter(matterId);
				if (!lastMatterLocation) return;

				const matterOverviewUrl =
					UrlService.Matter_Overview(matterId)?.toLowerCase();

				const shouldRedirect =
					lastMatterLocation?.toLowerCase() !== matterOverviewUrl && // lastMatterLocation isn't overview
					this.router.url?.toLowerCase() === matterOverviewUrl; // and the requested isn't the overview?
				if (!shouldRedirect) return;

				let url = lastMatterLocation;
				const indexOfModal = lastMatterLocation?.indexOf('modal=');
				if (indexOfModal > -1) {
					url = lastMatterLocation.substring(0, indexOfModal);
				}

				const [path, search] = url.split('?');
				const queryParams = search
					? Object.fromEntries(new URLSearchParams(search))
					: {};

				this.navigationService.goToUrl(path, queryParams);
			});

		this.store
			.select(UserProfileState.getCurrentBusinessId)
			.pipe(
				untilDestroyed(this),
				distinctUntilChanged(),
				filter((_, index) => index > 0),
			)
			.subscribe(() => {
				this.navigationService.goToDashboard();
			});

		this.matterHistory$
			.pipe(
				untilDestroyed(this),
				distinctUntilChanged(
					(prev, curr) => JSON.stringify(prev) === JSON.stringify(curr),
				),
			)
			.subscribe((history) => {
				if (history) {
					this.recentlyViewedDispatch.Create({
						...history,
						lastViewed: new Date().getTime(),
					});
				}
			});

		this.showMessage$ = combineLatest([
			this.showChicagoPayloadWarning$,
			this.showStewartPayloadWarning$,
			this.showFCTPayloadWarning$,
			this.showTitlePlusPayloadWarning$,
		]).pipe(
			map(
				([
					showChicagoPayloadWarning,
					showStewartPayloadWarning,
					showFCTPayloadWarning,
					showTitlePlusPayloadWarning,
				]) => {
					if (showChicagoPayloadWarning) {
						return 'Chicago';
					} else if (showStewartPayloadWarning) {
						return 'Stewart Title';
					} else if (showFCTPayloadWarning) {
						return 'FCT';
					} else if (showTitlePlusPayloadWarning) {
						return 'TitlePLUS';
					}

					return '';
				},
			),
		);

		this.handleMatterLoading();
	}

	private handleMatterLoading() {
		combineLatest([this.route.paramMap, this.matterLoading$])
			.pipe(
				untilDestroyed(this),
				map(([params, loading]) => {
					return {
						isForMatterScreen: isNotNullOrWhitespace(params.get('matterId')),
						matterId: params.get('matterId'),
						loading: loading,
					};
				}),
				filter((info) => info.isForMatterScreen),
			)
			.subscribe(async (info) => {
				if (info.loading) {
					this.loadingMessage = 'Loading matter...';
					return;
				}

				const selectedMatterId = this.store.selectSnapshot(SelectedMatter.id);
				if (info.matterId !== selectedMatterId) {
					return;
				}

				try {
					if (this.isLeapMatter) {
						Logger.info(
							`Matter ${this.matterId} is opening! Calling the backend to import changes from LEAP.`,
						);

						this.loadingMessage = 'Importing changes from LEAP...';
						const selectedLeapMatterId = this.store.selectSnapshot(
							SelectedMatter.getLeapMatterId,
						);
						await firstValueFrom(
							this.leapService.importLeapMatter(selectedLeapMatterId),
						);
						this.loadingMessage = '';

						Logger.info(
							`Matter ${this.matterId} is opening! Changes have been imported from LEAP.`,
						);
					}
				} catch (e) {
					Logger.error(
						`Unexpected error occurred during importing changes from LEAP for Matter ${this.matterId}. 
						Error from server: ${e.message}`,
					);
					this.snackbarService.error('Failed to import changes from LEAP.');

					throw e;
				} finally {
					this.matterLoading = false;
				}
			});
	}

	resubmitTitleInsurance(): void {
		this.navigationService.goToMatter_TitleInsurance_Submit(this.matterId);
	}

	ngOnDestroy(): void {
		this.matterDispatch.Get(null);
		this.renderer.setAttribute(this.document.body, 'file-type', 'none');
	}
}
